/**
 * Water-quality status: the watershed wash's color language.
 *
 * THE COLOR LAW (2026-08 revision): BLUE MEANS LITERAL WATER — the
 * rivers/lakes/ocean geometry drawn by the water overlay
 * (firefly-style.ts waterOverlayLayers) — and NOTHING ELSE. Watershed
 * polygons are LAND (drainage areas), so their status tints now live in
 * muted, non-water hues and sit at ground level in the visual hierarchy:
 * washes never compete with the marks (litter/cleanup light) or with the
 * water geometry itself.
 *
 * The previous revision deliberately made 'good' + 'unknown' a shared
 * blue, which — with 'unknown' the overwhelmingly dominant class — turned
 * every watershed on the map blue. That squatted on the strongest
 * convention in cartography (blue = water; the viewer actively infers
 * "water" from a blue polygon whether we like it or not — Schloss's
 * assignment-inference work, Lin et al. 2013) and left the map's actual
 * hydrography desaturated to near-black underneath. Reversed here at the
 * founder's direction: water bodies are blue, watershed washes are not.
 *
 * Hue assignments (all validated for deutan/protan separation and WCAG
 * 1.4.11 boundary contrast — see the palette notes in tokens.css):
 *   trash    → rust: the ONLY warm wash, deliberately in the litter-red
 *              family ("this water is EPA-listed for the thing this map
 *              hunts"), desaturated far below the marks so protan ΔE vs
 *              the litter hue stays wide.
 *   impaired → dim violet (unclaimed hue region, reads "flagged").
 *   good     → sage grey: near-neutral, recedes.
 *   unknown  → dark warm grey: near-invisible on the ground — an honest
 *              "no data" is quiet, and the dashed stroke still says
 *              "nobody assessed this" up close.
 *
 * Shared by explore.tsx (polygon fill + water-status pill) and the
 * lazily-loaded LegendBody, so the map, the pill, and the legend can
 * never disagree on status color or wording.
 */

/** EPA ATTAINS per-HUC12 assessment doc (wire shape, snake_case). */
export interface ImpairmentDoc {
  huc12: string;
  huc12_name: string;
  reporting_cycle: number;
  summary: {
    total_assessment_units: number;
    total_huc_area_sqmi: number;
    assessed_percent: number;
    impaired_percent: number;
    good_percent: number;
    unknown_percent: number;
  };
  impaired_assessment_units: Array<{
    assessment_unit: string;
    ir_category: string;
    causes: string[];
    probable_sources: string[];
  }>;
  top_causes: Array<{ cause: string; impaired_au_count: number }>;
  source: string;
  source_url: string;
  license: string;
  fetched_at: string;
}

export type WaterStatus = 'trash' | 'impaired' | 'good' | 'unknown';

/**
 * Status → hex. Keep in lockstep with the `--tt-*` tokens in
 * src/styles/tokens.css (the map is WebGL/JSON-styled and cannot read a
 * CSS custom property, so the values are duplicated, not referenced).
 *
 * NO BLUE HERE, by law — blue belongs to WATER_BODY_* below. Boundary
 * strokes at these values measure 4.12 / 4.05 / 3.49:1 over the #121212
 * ground (trash/impaired/good); 'unknown' is deliberately whisper-quiet.
 */
export const WATER_STATUS_COLOR: Record<WaterStatus, string> = {
  trash: '#C2543A',
  impaired: '#7E6BA6',
  good: '#5F6E67',
  unknown: '#3F3F3B',
};

/**
 * Readable INK variants of the status hues for text chips/pills on the
 * dark popup surfaces — the wash hues above are tuned for polygon fills
 * at low opacity, not for 12px text.
 */
export const WATER_STATUS_INK: Record<WaterStatus, string> = {
  trash: '#E08A6E',
  impaired: '#A99BC9',
  good: '#9DABA2',
  unknown: '#8F8B84',
};

/**
 * LITERAL water — the hydrography overlay (rivers, lakes, ocean), the
 * only blue on the map. LINE is the waterway stroke (4.03:1 over the
 * ground); FILL is the polygon body, deliberately dimmer than every mark
 * so water stays ground, not figure.
 */
export const WATER_BODY_LINE = '#2A78BD';
export const WATER_BODY_FILL = '#16405F';

/**
 * The second visual variable that carries what colour no longer can.
 * `solid` = EPA has assessed this water and found it healthy; `dashed` =
 * nobody has assessed it. Costs zero contrast budget, unlike a fourth hue.
 *
 * Consumed as a MapLibre `line-dasharray` match in tile-ladder-style.ts
 * and as a CSS/SVG dash pattern on the legacy Leaflet polygons.
 */
export const WATER_STATUS_STROKE: Record<WaterStatus, 'solid' | 'dashed'> = {
  trash: 'solid',
  impaired: 'solid',
  good: 'solid',
  unknown: 'dashed',
};

/**
 * Impaired-FOR-TRASH is the product's headline state, so it never rides
 * on colour alone: it also gets a doubled boundary stroke. (A 45° hatch
 * fill-pattern is the follow-up — MapLibre has no vector hatch primitive,
 * so it needs an inline data-URI sprite.)
 */
export const WATER_STATUS_LINE_WIDTH_MULT: Record<WaterStatus, number> = {
  trash: 2,
  impaired: 1,
  good: 1,
  unknown: 1,
};
export const WATER_STATUS_LABEL: Record<WaterStatus, string> = {
  trash: 'Impaired — for trash',
  impaired: 'Impaired',
  good: 'Assessed — good',
  unknown: 'Not assessed',
};
// Compact status text for the always-visible pill (full label lives in
// the expanded banner). Empty for 'unknown' so the pill stays quiet when
// there's no assessment to report.
export const WATER_STATUS_SHORT: Record<WaterStatus, string> = {
  trash: 'impaired · trash',
  impaired: 'impaired',
  good: 'healthy',
  unknown: '',
};

export function waterStatusOf(imp: ImpairmentDoc | null | undefined): WaterStatus {
  if (!imp) return 'unknown';
  // The persisted doc carries a clean is_impaired boolean; fall back to
  // the assessment-unit list / impaired-area percent if it's absent.
  const impaired =
    (imp as { is_impaired?: boolean }).is_impaired === true ||
    (imp.impaired_assessment_units?.length ?? 0) > 0 ||
    (imp.summary?.impaired_percent ?? 0) > 0;
  if (impaired) {
    // "For trash" only when the impairing cause literally names trash —
    // the ATTAINS parameter-group trash flag isn't persisted per-cause,
    // so most impaired waters read as generic "impaired" until the
    // backend stores an explicit impaired_for_trash flag.
    const isTrash =
      (imp.top_causes ?? []).some((c) =>
        String(c?.cause ?? '').toUpperCase().includes('TRASH'),
      ) ||
      (imp.impaired_assessment_units ?? []).some((u) =>
        (u?.causes ?? []).some((c) => String(c).toUpperCase().includes('TRASH')),
      );
    return isTrash ? 'trash' : 'impaired';
  }
  if (
    (imp.summary?.assessed_percent ?? 0) > 0 ||
    (imp.summary?.total_assessment_units ?? 0) > 0
  ) {
    return 'good';
  }
  return 'unknown';
}
